import React, { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Trophy, Music, Sparkles, Heart, Check, ArrowRight } from "lucide-react";
import { Modal } from "../common/Modal";
import { usePartyStore } from "../../stores/partyStore";
import { useFavorites } from "../../hooks/useFavorites";
import { UserAvatar } from "../common/UserAvatar";

export const PartyWrappedModal: React.FC = () => {
  const { partyEndedSummary, setPartyEndedSummary } = usePartyStore();
  const { addFavorite } = useFavorites();
  const [exported, setExported] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Reset export state and fire confetti whenever a new summary is presented
  useEffect(() => {
    if (partyEndedSummary) {
      setExported(false);
      setExporting(false);

      // Fire celebration confetti!
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
      });
    }
  }, [partyEndedSummary]);

  if (!partyEndedSummary) return null;

  const handleExportPlaylist = async () => {
    setExporting(true);
    try {
      for (const song of partyEndedSummary.allSongs) {
        await addFavorite(song);
      }
      setExported(true);
    } catch (err) {
      console.error("Failed to export songs:", err);
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    setExported(false);
    setExporting(false);
    setPartyEndedSummary(null);
  };

  const topSinger = partyEndedSummary.topSingers.length > 0 ? partyEndedSummary.topSingers[0] : null;

  return (
    <Modal isOpen={Boolean(partyEndedSummary)} onClose={handleClose} title="🎉 Party Wrapped!">
      <div className="space-y-6 text-center py-2">
        {/* MVP Singer Spotlight */}
        {topSinger && (
          <div className="bg-gradient-to-tr from-primary/20 via-card to-accent/20 border border-primary/40 p-6 rounded-3xl space-y-3 shadow-xl">
            <div className="relative inline-block">
              <UserAvatar
                photoURL={topSinger.photoURL}
                name={topSinger.name}
                size="xl"
                className="mx-auto"
              />
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-yellow-400 text-background flex items-center justify-center shadow-md">
                <Trophy className="w-5 h-5 fill-background" />
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-widest text-primary flex items-center justify-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Karaoke MVP
              </span>
              <h3 className="text-2xl font-bold font-heading text-text-main">
                {topSinger.name}
              </h3>
              <p className="text-xs text-text-muted">
                Belted out <strong className="text-accent font-bold">{topSinger.count}</strong> {topSinger.count === 1 ? "song" : "songs"}!
              </p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 rounded-2xl bg-card border border-border flex flex-col items-center justify-center space-y-1">
            <Music className="w-6 h-6 text-accent" />
            <span className="text-2xl font-bold font-mono text-text-main">
              {partyEndedSummary.totalSongsSung}
            </span>
            <span className="text-[11px] text-text-muted font-medium">Total Songs Sung</span>
          </div>

          <div className="p-4 rounded-2xl bg-card border border-border flex flex-col items-center justify-center space-y-1">
            <Trophy className="w-6 h-6 text-yellow-400" />
            <span className="text-2xl font-bold font-mono text-text-main">
              {partyEndedSummary.topSingers.length}
            </span>
            <span className="text-[11px] text-text-muted font-medium">Active Singers</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          {partyEndedSummary.allSongs.length > 0 && (
            <button
              onClick={handleExportPlaylist}
              disabled={exporting || exported}
              className={`w-full py-3.5 px-5 rounded-2xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer ${
                exported
                  ? "bg-green-600/20 text-green-400 border border-green-500/40"
                  : "bg-card border border-border hover:border-accent text-accent hover:text-accent-hover"
              }`}
            >
              {exported ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Saved to Your Favorites!</span>
                </>
              ) : (
                <>
                  <Heart className="w-4 h-4" />
                  <span>{exporting ? "Saving Songs..." : "Export Party Songs to My Favorites"}</span>
                </>
              )}
            </button>
          )}

          <button
            onClick={handleClose}
            className="w-full py-3.5 px-5 rounded-2xl bg-primary hover:bg-primary-hover text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-lg shadow-primary/20 cursor-pointer"
          >
            <span>Return to Lobby</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Modal>
  );
};
